/**
 * Peças da Equipe por time (H129, spec 24) usadas pelas abas Times e Pessoas: o selo de presença, a bolinha de "com
 * a tela aberta", e as duas ações do supervisor na linha da pessoa — Pôr em pausa/offline e Redistribuir.
 *
 * Sem "em pausa há 40 min" e sem ranking: a decisão do owner no H32 (presença não vira métrica de produtividade).
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Botao } from "@hugi/ui";
import { Dialogo, Menu, Segmentado, Selo, type Tom } from "@hugi/ui/kit";
import { Coffee, MoreHorizontal, Power, Shuffle, UserCheck } from "lucide-react";
import { mudarPresenca, redistribuirConversas, type Disponibilidade, type EstadoDePresenca } from "./dados.js";

export const MOTIVOS_PADRAO = ["Almoço", "Reunião", "Intervalo", "Outro"] as const;

/** Disponibilidade → como a tela mostra. "Fora da jornada" é offline posto pela jornada, e a tela diz. */
export function rotuloDaDisponibilidade(d: Disponibilidade | undefined): { tom: Tom; rotulo: string } {
  if (!d) return { tom: "neutro", rotulo: "—" };
  if (d.estado === "inativo") return { tom: "perigo", rotulo: "Inativo" };
  if (d.estado === "disponivel") return { tom: "sucesso", rotulo: "Disponível" };
  if (d.estado === "pausa") return { tom: "atencao", rotulo: d.motivo ? `Pausa · ${d.motivo}` : "Em pausa" };
  return { tom: "neutro", rotulo: d.origem === "jornada" ? "Fora da jornada" : d.motivo ? `Offline · ${d.motivo}` : "Offline" };
}

export function SeloDePresenca(props: { readonly disponibilidade: Disponibilidade | undefined }) {
  const r = rotuloDaDisponibilidade(props.disponibilidade);
  return <Selo tom={r.tom}>{r.rotulo}</Selo>;
}

/** A bolinha de "com o atendimento aberto agora" (C5). Desconhecida não desenha nada — não é "offline". */
export function PontoNaTela(props: { readonly naTela: boolean | null | undefined }) {
  if (props.naTela === null || props.naTela === undefined) return null;
  const rotulo = props.naTela ? "Com o atendimento aberto" : "Sem o atendimento aberto";
  return <span role="img" aria-label={rotulo} title={rotulo} className={`hugi-equipe-ponto${props.naTela ? " is-ligado" : ""}`} />;
}

/** "2 de 5" com limite, "2" sem. No limite, em destaque. */
export function Carga(props: { readonly abertos: number; readonly limite: number | null | undefined }) {
  const cheio = props.limite !== null && props.limite !== undefined && props.abertos >= props.limite;
  return (
    <span className={`hugi-equipe-carga${cheio ? " is-cheia" : ""}`} title={cheio ? "No limite: não pega conversa nova" : "Conversas abertas agora"}>
      {props.limite ? `${props.abertos} de ${props.limite}` : props.abertos}
    </span>
  );
}

/** Duração curta: "8 min", "2 h 10", "3 d". */
export function duracaoCurta(segundos: number): string {
  if (segundos < 3600) return `${Math.max(1, Math.round(segundos / 60))} min`;
  if (segundos < 86400) {
    const h = Math.floor(segundos / 3600);
    const m = Math.round((segundos % 3600) / 60);
    return m ? `${h} h ${m}` : `${h} h`;
  }
  return `${Math.floor(segundos / 86400)} d`;
}

export interface AlvoDoSupervisor {
  readonly atendenteId: string;
  readonly nome: string | null;
  readonly abertos: number;
  readonly disponibilidade?: Disponibilidade;
}

/**
 * O menu do supervisor na linha da pessoa: Pôr em pausa, Pôr offline ou Disponível, e Redistribuir. Quem chama decide
 * se mostra (administra a equipe, ou é responsável por um time dela); o servidor confere de novo.
 */
export function MenuDoSupervisor(props: {
  readonly alvo: AlvoDoSupervisor;
  readonly podeRedistribuir: boolean;
  readonly destinos: readonly { readonly id: string; readonly nome: string | null; readonly disponibilidade?: Disponibilidade }[];
  readonly motivos: readonly string[];
}) {
  const [aberto, setAberto] = useState<null | "presenca" | "redistribuir">(null);
  const [estado, setEstado] = useState<EstadoDePresenca>("pausa");
  const nome = props.alvo.nome ?? "esta pessoa";
  const itens = [
    { id: "pausa", rotulo: "Pôr em pausa…", icone: <Coffee size={14} />, aoEscolher: () => (setEstado("pausa"), setAberto("presenca")) },
    { id: "offline", rotulo: "Pôr offline…", icone: <Power size={14} />, aoEscolher: () => (setEstado("offline"), setAberto("presenca")) },
    ...(props.alvo.disponibilidade?.estado === "pausa" || props.alvo.disponibilidade?.estado === "offline"
      ? [{ id: "disponivel", rotulo: "Voltar a Disponível", icone: <UserCheck size={14} />, aoEscolher: () => (setEstado("disponivel"), setAberto("presenca")) }]
      : []),
    ...(props.podeRedistribuir
      ? [{
          id: "redistribuir",
          rotulo: "Redistribuir conversas…",
          icone: <Shuffle size={14} />,
          aoEscolher: () => setAberto("redistribuir"),
          desabilitado: props.alvo.abertos === 0,
        }]
      : []),
  ];
  return (
    <>
      <Menu
        rotulo={`Ações sobre ${nome}`}
        classeDoBotao="v2-botao-icone"
        conteudoDoBotao={<MoreHorizontal size={15} aria-hidden="true" />}
        itens={itens}
      />
      <Dialogo aberto={aberto === "presenca"} aoFechar={() => setAberto(null)} titulo={`Presença de ${nome}`} largura="estreita">
        <FormDePresenca alvoId={props.alvo.atendenteId} inicial={estado} motivos={props.motivos} aoConcluir={() => setAberto(null)} />
      </Dialogo>
      <Dialogo aberto={aberto === "redistribuir"} aoFechar={() => setAberto(null)} titulo={`Redistribuir as conversas de ${nome}`} largura="estreita">
        <FormDeRedistribuir alvo={props.alvo} destinos={props.destinos} aoConcluir={() => setAberto(null)} />
      </Dialogo>
    </>
  );
}

/** Disponível / Em pausa (motivo da lista) / Offline. A própria pessoa usa o mesmo formulário no atendimento. */
export function FormDePresenca(props: {
  readonly alvoId: string;
  readonly inicial: EstadoDePresenca;
  readonly motivos: readonly string[];
  readonly aoConcluir: (aviso: string) => void;
}) {
  const cliente = useQueryClient();
  const [estado, setEstado] = useState<EstadoDePresenca>(props.inicial);
  const [motivo, setMotivo] = useState<string>(props.motivos[0] ?? "Almoço");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await mudarPresenca(cliente, props.alvoId, estado, estado === "disponivel" ? null : estado === "pausa" ? motivo : motivo === "Outro" ? null : motivo);
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não deu para mudar a presença.");
      return;
    }
    props.aoConcluir(estado === "disponivel" ? "Disponível." : estado === "pausa" ? `Em pausa: ${motivo}.` : "Offline.");
  };

  return (
    <div className="hugi-equipe-form">
      <Segmentado
        rotulo="Presença"
        ativa={estado}
        aoTrocar={(id) => setEstado(id as EstadoDePresenca)}
        opcoes={[
          { id: "disponivel", rotulo: "Disponível" },
          { id: "pausa", rotulo: "Em pausa" },
          { id: "offline", rotulo: "Offline" },
        ]}
      />
      {estado !== "disponivel" ? (
        <label className="hugi-equipe-campo">
          <span>{estado === "pausa" ? "Motivo" : "Motivo (opcional)"}</span>
          <select value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo">
            {props.motivos.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {erro ? <p role="alert" className="hugi-equipe-erro">{erro}</p> : null}
      <div className="hugi-equipe-acoes">
        <Botao onClick={() => void salvar()} desabilitado={ocupado}>
          {ocupado ? "Salvando…" : "Confirmar"}
        </Botao>
      </div>
    </div>
  );
}

function FormDeRedistribuir(props: {
  readonly alvo: AlvoDoSupervisor;
  readonly destinos: readonly { readonly id: string; readonly nome: string | null; readonly disponibilidade?: Disponibilidade }[];
  readonly aoConcluir: () => void;
}) {
  const cliente = useQueryClient();
  const [para, setPara] = useState("fila");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const opcoes = props.destinos.filter((d) => d.id !== props.alvo.atendenteId);

  const confirmar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await redistribuirConversas(cliente, props.alvo.atendenteId, para);
    setOcupado(false);
    if (!r.ok) {
      // A frase do servidor diz quem não recebe e por quê (pausa, jornada, limite).
      setErro(r.falha?.erro.mensagem ?? "Não deu para redistribuir.");
      return;
    }
    props.aoConcluir();
  };

  const n = props.alvo.abertos;
  return (
    <div className="hugi-equipe-form">
      <label className="hugi-equipe-campo">
        <span>Para onde vão as {n === 1 ? "1 conversa" : `${n} conversas`}</span>
        <select value={para} onChange={(e) => setPara(e.target.value)} aria-label="Destino das conversas">
          <option value="fila">De volta à fila de cada uma</option>
          {opcoes.map((d) => (
            <option key={d.id} value={d.id} disabled={d.disponibilidade !== undefined && d.disponibilidade.estado !== "disponivel"}>
              {d.nome ?? "Sem nome"}
              {d.disponibilidade && d.disponibilidade.estado !== "disponivel" ? ` (${rotuloDaDisponibilidade(d.disponibilidade).rotulo})` : ""}
            </option>
          ))}
        </select>
      </label>
      {erro ? <p role="alert" className="hugi-equipe-erro">{erro}</p> : null}
      <div className="hugi-equipe-acoes">
        <Botao onClick={() => void confirmar()} desabilitado={ocupado}>
          {ocupado ? "Redistribuindo…" : `Redistribuir ${n === 1 ? "1 conversa" : `${n} conversas`}`}
        </Botao>
      </div>
    </div>
  );
}
